// src/components/rodeoAi/QuestionBank.jsx
import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Curated questions based on available functions
const QUESTION_POOL = {
    simple: [
        "What's the current price of Apple?",
        "Show me today's market overview",
        "What are the top performing sectors?",
        "What's Tesla's stock price?",
        "How is the S&P 500 doing today?",
        "What's the VIX at?",
        "Show me Microsoft's latest news",
        "What's Bitcoin's current price?"
    ],
    medium: [
        "Compare Tesla vs BYD stock performance",
        "Analyze AMD's financial health",
        "What's the latest news on Nvidia?",
        "Show me Apple's technical indicators",
        "What are analyst ratings for Meta?",
        "Compare Microsoft vs Google financials",
        "What insider trading happened recently for Tesla?",
        "Show me Pelosi's latest stock trades"
    ],
    complex: [
        "Deep dive on Microsoft - financials, technicals, and analyst ratings",
        "Why did Meta's stock move today?",
        "Compare Apple vs Samsung - comprehensive analysis",
        "Analyze Netflix's financials and recent earnings",
        "Show me tech sector performance and top 3 stocks",
        "What's happening with AI stocks today?",
        "Analyze my portfolio performance and risks",
        "Compare dividend yields: AT&T vs Verizon vs T-Mobile"
    ]
};

const QuestionBank = ({ onSelectQuestion, mode = 'full' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedQuestions, setSelectedQuestions] = useState([]);

    // Randomly select questions based on screen size
    useEffect(() => {
        const selectRandomQuestions = () => {
            const simple = QUESTION_POOL.simple[Math.floor(Math.random() * QUESTION_POOL.simple.length)];
            const medium1 = QUESTION_POOL.medium[Math.floor(Math.random() * QUESTION_POOL.medium.length)];
            const medium2 = QUESTION_POOL.medium[Math.floor(Math.random() * QUESTION_POOL.medium.length)];
            const complex = QUESTION_POOL.complex[Math.floor(Math.random() * QUESTION_POOL.complex.length)];

            // Ensure medium questions are different
            let finalMedium2 = medium2;
            if (medium1 === medium2) {
                const alternatives = QUESTION_POOL.medium.filter(q => q !== medium1);
                finalMedium2 = alternatives[Math.floor(Math.random() * alternatives.length)];
            }

            // On mobile, show only 2 questions to save space
            if (isMobile) {
                setSelectedQuestions([simple, medium1]);
            } else {
                setSelectedQuestions([simple, medium1, finalMedium2, complex]);
            }
        };

        selectRandomQuestions();
    }, [isMobile]);

    if (selectedQuestions.length === 0) return null;

    return (
        <Box
            sx={{
                mb: { xs: 2, sm: 3 },
                px: { xs: 0, sm: 0.5 }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 2 } }}>
                <AutoAwesomeIcon sx={{ 
                    color: 'primary.main', 
                    fontSize: { xs: 18, sm: 20 } 
                }} />
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    Try asking...
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: mode === 'mini' ? '1fr' : 'repeat(2, 1fr)'
                    },
                    gap: { xs: 1, sm: 1.5 }
                }}
            >
                {selectedQuestions.map((question, index) => (
                    <Paper
                        key={index}
                        elevation={0}
                        sx={(theme) => ({
                            p: { xs: 1.5, sm: 2 },
                            cursor: 'pointer',
                            backgroundColor:
                                theme.palette.mode === 'dark'
                                    ? 'rgba(19, 47, 76, 0.4)'
                                    : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border:
                                theme.palette.mode === 'dark'
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : '1px solid rgba(0, 0, 0, 0.08)',
                            borderRadius: { xs: 1.5, sm: 2 },
                            transition: 'all 0.2s ease-in-out',
                            // Add touch feedback for mobile
                            WebkitTapHighlightColor: 'transparent',
                            '&:active': {
                                transform: 'scale(0.98)',
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(0, 127, 255, 0.12)'
                                        : 'rgba(0, 127, 255, 0.05)'
                            },
                            '&:hover': {
                                transform: isMobile ? 'none' : 'translateY(-2px)',
                                boxShadow:
                                    theme.palette.mode === 'dark'
                                        ? '0 8px 24px rgba(0,127,255,0.15)'
                                        : '0 4px 16px rgba(0,127,255,0.1)',
                                borderColor: 'primary.main',
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(0, 127, 255, 0.08)'
                                        : 'rgba(0, 127, 255, 0.03)'
                            }
                        })}
                        onClick={() => onSelectQuestion(question)}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                lineHeight: 1.4,
                                color: 'text.primary',
                                fontSize: { xs: '0.813rem', sm: '0.875rem' }
                            }}
                        >
                            {question}
                        </Typography>
                    </Paper>
                ))}
            </Box>
        </Box>
    );
};

export default QuestionBank;